/**
 * Types and calls for this surface, on the shared request wrapper.
 *
 * This file used to carry its own byte-identical copy of `request` from
 * lib/api.ts, because it was written while four agents held the tree and
 * lib/api.ts was another one's file. Two copies of the wrapper that turns a
 * non-2xx into a thrown Error is two places to fix when the error contract
 * changes, and the copy would have drifted the first time it did.
 */

import { request } from '../lib/api.js';

export type Grounding = 'stated' | 'nes' | 'ungrounded';

export type FigureBehaviour = 'load_proportionate' | 'same_for_all' | 'rate' | 'unknown';

export interface EntitlementFigure {
  id: string;
  type: string;
  clause: string;
  clauseHeading: string | null;
  quantum: number;
  unit: string;
  grounding: Grounding;
  behaviour: FigureBehaviour;
  cite: string;
  because: string | null;
  calculable: boolean;
  blockedBecause: string | null;
  verifiedAt: string | null;
}

export interface WorkingStep {
  label: string;
  value: string;
  from: string | null;
}

export interface CalculatorResult {
  value: number;
  unit: string;
  working: WorkingStep[];
  basis: { clause: string; expression: string; recoveredFrom: string | null };
  caveats: string[];
}

export interface CalculatorListing {
  calculators: {
    id: string;
    title: string;
    clause: string | null;
    available: boolean;
    unavailableBecause: string | null;
  }[];
  entitlements: {
    total: number;
    verified: number;
    withQuantum?: number;
    calculable?: number;
  };
}

export const calculatorApi = {
  listing: () => request<CalculatorListing>('GET', '/calculators'),

  figures: () =>
    request<{ figures: EntitlementFigure[]; withoutQuantum: number }>(
      'GET',
      '/calculators/entitlements',
    ),

  entitlement: (body: { entitlementId: string; load: number; amount?: number }) =>
    request<CalculatorResult>('POST', '/calculators/entitlement', body),

  proRata: (body: {
    salaryForAttendanceWeeks: number;
    attendanceWeeksInYear: number;
    nonAttendanceWeeksInYear: number;
    salaryAlreadyPaidForNonAttendance: number;
  }) => request<CalculatorResult>('POST', '/calculators/pro-rata', body),
};
